import { useEffect } from "react";
import { faTag } from "@fortawesome/pro-thin-svg-icons";

import Icon from "common/components/Icon";
import { Feature } from "common/types";
import { WithStringValues } from "common/utils/types";
import { useListSearchState } from "common/hooks";

import Error404Page from "client/common/components/errors/Error404Page";
import Page from "client/common/components/layouts/Page";
import Body from "client/common/components/layouts/Body";
import Container from "client/common/components/layouts/Container";
import Results from "client/common/components/results/Results";
import { useFeatures } from "client/clinics/hooks";
import { ProductListSource } from "client/analytics/types";
import { Paths } from "client/routes";

import { useProductDealsSearchQuery, useProductFilterOptionState } from "../hooks";
import { PRODUCT_DEALS_FILTER_SCOPE } from "../hooks/constants";
import { getProductFiltersFromSearch } from "../utils";
import { ProductDealsSearch } from "../types";
import ProductCardList from "./ProductCardList";
import ProductCardListHeader from "./ProductCardListHeader";

import styles from "../styles/DealsPage.module.less";

const initialSearch = {
  category_ids: [],
  manufacturer_ids: [],
  page: 1,
  page_size: 20,
  prescription_required: [],
  sort: "",
  species: [],
};

type DealsResultsProps = {
  search: WithStringValues<ProductDealsSearch>;
  onUpdateSearch: (search: Partial<ProductDealsSearch>) => void;
};

const DealsResults = ({ search, onUpdateSearch }: DealsResultsProps) => {
  const { data: products, isLoading, error, isRefetching } = useProductDealsSearchQuery(search);
  const { data: filterOptions } = useProductFilterOptionState(PRODUCT_DEALS_FILTER_SCOPE);

  if (isLoading) {
    return <Results.Loading description="Loading deals" />;
  }

  if (error || !products) {
    return <Results.FetchError error={error} entityName="deal" />;
  }

  const filters = getProductFiltersFromSearch({ ...search, q: "" });

  return (
    <div className={styles.container}>
      <ProductCardListHeader
        paginator={products.paginator}
        isRefetching={isRefetching}
        entityName="deal"
        options={filterOptions?.filters}
        filters={filters}
        onChange={onUpdateSearch}
      />
      {products.results.length ? (
        <ProductCardList
          results={products.results}
          listSource={ProductListSource.DealsPage}
          searchTerm={null}
          paginator={products.paginator}
          onPageChange={(page) => onUpdateSearch({ page })}
          entityName="deal"
        />
      ) : (
        <Results.None
          header="No deals found"
          description="Deals change often. Check back soon for new eligible savings."
        />
      )}
    </div>
  );
};

const DealsPage = () => {
  const [search, updateSearch] = useListSearchState<Paths["products"]["deals"]>(initialSearch);
  const features = useFeatures();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [search.page]);

  if (!features[Feature.Deals]) {
    return <Error404Page />;
  }

  return (
    <Page className={styles.page}>
      <div>
        <Container>
          <div className={styles.top}>
            <div className={styles.heading}>
              <h1 className={styles.title}>Deals</h1>
              <div className={styles.description}>
                Save on your pet&apos;s care with autoship and manufacturer discounts, plus
                limited-time offers, all in one place.
              </div>
            </div>
            <Icon className={styles.icon} icon={faTag} />
          </div>
        </Container>
        <div className={styles.main}>
          <Body>
            <DealsResults search={search} onUpdateSearch={updateSearch} />
          </Body>
        </div>
      </div>
    </Page>
  );
};

export default DealsPage;
